import { createElement, useEffect, useRef } from 'react';
import './InlineEdit.scss';

interface InlineTextAreaProps {
  value: string;
  onChange: (next: string) => void;
  renderTag?: string;
  renderClassName?: string;
  placeholder?: string;
  ariaLabel?: string;
}

const InlineTextArea = ({
  value,
  onChange,
  renderTag = 'div',
  renderClassName,
  placeholder,
  ariaLabel,
}: InlineTextAreaProps) => {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value;
    }
  }, [value]);

  const handleInput = () => {
    if (!ref.current) return;
    const next = ref.current.innerText;
    if (next !== value) onChange(next);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    handleInput();
  };

  if (typeof window === 'undefined') {
    return createElement(renderTag, { className: renderClassName }, value);
  }

  const className = ['inline-textarea', renderClassName]
    .filter(Boolean)
    .join(' ');

  return createElement(renderTag, {
    ref,
    className,
    contentEditable: true,
    suppressContentEditableWarning: true,
    spellCheck: true,
    'data-placeholder': placeholder,
    'aria-label': ariaLabel,
    'aria-multiline': true,
    onInput: handleInput,
    onPaste: handlePaste,
  });
};

export default InlineTextArea;
